import { forwardRef, type ReactNode, type KeyboardEvent } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

/*
 * Card — VOLL DS v2 panel shell.
 *
 * Examples:
 *   <Card>...</Card>
 *   <Card tone="red" surface="paper">...</Card>
 *   <Card onTap={open}>...</Card>
 *   <Card onTap={open} active>...</Card>
 *
 * The card is a simple bordered box. The tone variant picks the
 * border colour, the surface variant picks the background, and an
 * optional `onTap` turns it into a button-like region with hover
 * affordance. `active` always wins the border colour — terracotta —
 * so callers can light up the selected card regardless of tone.
 *
 * The component does not accept a `className` prop. Every visual
 * decision lives in the variant table.
 */
const cardVariants = cva('rounded-lg border bg-clip-padding', {
  variants: {
    tone: {
      neutral: 'border-border-soft',
      amber: 'border-tone-amber-fg/25',
      red: 'border-tone-red-fg/25',
      ai: 'border-ai/25',
      green: 'border-tone-green-fg/25',
      brand: 'border-primary/25',
    },
    surface: {
      default: 'bg-surface',
      paper: 'bg-paper',
      brand: 'bg-primary-tint/45',
    },
    interactive: {
      true: 'cursor-pointer transition-colors duration-base ease-standard hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
      false: '',
    },
    active: {
      true: 'border-primary',
      false: '',
    },
  },
  defaultVariants: {
    tone: 'neutral',
    surface: 'default',
    interactive: false,
    active: false,
  },
});

type CardTone = NonNullable<VariantProps<typeof cardVariants>['tone']>;
type CardSurface = NonNullable<VariantProps<typeof cardVariants>['surface']>;

interface CardProps {
  tone?: CardTone;
  surface?: CardSurface;
  active?: boolean;
  onTap?: () => void;
  /** Forwarded to the rendered element when `onTap` makes the card a button. */
  'aria-label'?: string;
  'data-testid'?: string;
  children: ReactNode;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    tone,
    surface,
    active = false,
    onTap,
    'aria-label': ariaLabel,
    'data-testid': testId,
    children,
  },
  ref,
) {
  const interactive = onTap != null;
  const className = cardVariants({ tone, surface, interactive, active });

  if (!interactive) {
    return (
      <div ref={ref} className={className} data-testid={testId}>
        {children}
      </div>
    );
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onTap();
    }
  };

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      data-testid={testId}
      onClick={onTap}
      onKeyDown={handleKeyDown}
      className={className}
    >
      {children}
    </div>
  );
});
